/**
 * The app's shortcut catalogue. Every key binding in Viko is declared here (the cheat sheet, the
 * palette hints, tooltips and `aria-keyshortcuts` all read from it). Rules of the road:
 *  - Plain keys only when the user isn't typing (the runtime enforces it); anything that must work
 *    inside a text field carries ⌥ or ⌘/Ctrl.
 *  - ⌥ is the "act on this dialog/composer" modifier; ⌘/Ctrl+Enter submits; Esc closes.
 *  - Never ⌘N/T/W/L/R/1–9 (browser owned; see `reservedReason`). Lanes tabs use ⌥1–9.
 *  - `external: true` entries are handled by their component's own listener.
 */
import { ShortcutRegistry } from './registry.js';
const digits = (mod) => Array.from({ length: 9 }, (_, i) => `${mod}${i + 1}`);
export const SCOPE_TITLES = {
    global: 'Everywhere',
    page: 'On this screen',
    dialog: 'In dialogs',
    component: 'In the message box',
};
export const SHORTCUT_DEFS = [
    // ---- global
    {
        id: 'global.palette',
        scope: 'global',
        group: 'Navigate',
        label: 'Command palette',
        keys: ['Mod+K'],
        inDialogs: true,
    },
    { id: 'global.capture', scope: 'global', group: 'Create', label: 'Capture an idea', keys: ['c'] },
    { id: 'global.newSession', scope: 'global', group: 'Create', label: 'New session', keys: ['n'] },
    { id: 'global.help', scope: 'global', group: 'Help', label: 'Keyboard shortcuts', keys: ['?'] },
    {
        // viko-call: tap to start a call with the mic on (or mute during one); hold to talk.
        // Handled by the call provider's own listener (it needs key-up for hold-to-talk).
        id: 'global.call',
        scope: 'global',
        group: 'Create',
        label: 'Call Viko (tap) · hold to talk',
        keys: ['Mod+Shift+V'],
        external: true,
    },
    {
        // Only while this browser's encrypted work is locked (the 🔒 Locked · Unlock tab chip).
        id: 'global.unlock',
        scope: 'global',
        group: 'Help',
        label: 'Unlock your work in this browser',
        keys: ['Shift+U'],
    },
    {
        id: 'global.focusInput',
        scope: 'global',
        group: 'Navigate',
        label: 'Focus the message box',
        keys: ['/'],
        external: true,
    },
    {
        id: 'global.go.now',
        scope: 'global',
        group: 'Navigate',
        label: 'Home › Running',
        keys: ['g n'],
        external: true,
    },
    {
        id: 'global.go.inbox',
        scope: 'global',
        group: 'Navigate',
        label: 'Home › Needs you',
        keys: ['g i'],
        external: true,
    },
    {
        id: 'global.go.projects',
        scope: 'global',
        group: 'Navigate',
        label: 'Home › Projects',
        keys: ['g o'],
        external: true,
    },
    {
        id: 'global.go.you',
        scope: 'global',
        group: 'Navigate',
        label: 'Settings',
        keys: ['g ,'],
        external: true,
    },
    {
        id: 'global.go.overview',
        scope: 'global',
        group: 'Navigate',
        label: 'Home',
        keys: ['g 0'],
        external: true,
    },
    {
        id: 'global.go.tab',
        scope: 'global',
        group: 'Navigate',
        label: 'Go to project tab 1–9',
        keys: ['g 1'],
        display: 'g 1–9',
        external: true,
    },
    // ---- lanes / project tabs
    {
        id: 'global.home',
        scope: 'global',
        group: 'Navigate',
        label: 'Home tab',
        keys: ['Alt+0'],
        external: true,
    },
    {
        id: 'project.tab',
        scope: 'page:project',
        group: 'Project lanes',
        label: 'Switch project tab 1–9',
        keys: digits('Alt+'),
        display: 'Alt+1–9',
        external: true,
    },
    {
        id: 'project.tab.installed',
        scope: 'page:project',
        group: 'Project lanes',
        label: 'Switch project tab (installed app)',
        keys: digits('Mod+'),
        display: 'Mod+1–9',
        external: true,
        standalone: true,
    },
    {
        id: 'project.tab.move',
        scope: 'page:project',
        group: 'Project lanes',
        label: 'Previous / next tab',
        keys: ['Alt+Shift+ArrowLeft', 'Alt+Shift+ArrowRight'],
        display: 'Alt+Shift+←/→',
        external: true,
    },
    {
        id: 'project.card',
        scope: 'page:project',
        group: 'Project lanes',
        label: 'Move between cards',
        keys: ['j', 'k', 'h', 'l'],
        display: 'j k h l',
        external: true,
    },
    {
        id: 'project.lane',
        scope: 'page:project',
        group: 'Project lanes',
        label: 'Previous / next lane',
        keys: ['[', ']'],
        display: '[ ]',
        external: true,
    },
    {
        id: 'project.ask',
        scope: 'page:project',
        group: 'Project lanes',
        label: 'Open the card’s ask',
        keys: ['a'],
        external: true,
    },
    {
        id: 'project.bark',
        scope: 'page:project',
        group: 'Project lanes',
        label: 'Bark box',
        keys: ['b'],
        external: true,
    },
    // ---- inbox / now
    {
        id: 'inbox.move',
        scope: 'page:inbox',
        group: 'Inbox and Now',
        label: 'Next / previous item',
        keys: ['j', 'k'],
        display: 'j k',
        external: true,
    },
    {
        id: 'inbox.open',
        scope: 'page:inbox',
        group: 'Inbox and Now',
        label: 'Open item',
        keys: ['Enter'],
        external: true,
    },
    {
        id: 'inbox.quick',
        scope: 'page:inbox',
        group: 'Inbox and Now',
        label: 'Quick pick 1–9',
        keys: ['1'],
        display: '1–9',
        external: true,
    },
    {
        id: 'inbox.act',
        scope: 'page:inbox',
        group: 'Inbox and Now',
        label: 'Snooze, reply, dismiss, done',
        keys: ['s', 'r', 'd', 'e'],
        display: 's r d e',
        external: true,
    },
    {
        id: 'inbox.select',
        scope: 'page:inbox',
        group: 'Inbox and Now',
        label: 'Select, extend selection',
        keys: ['x', 'Shift+X'],
        display: 'x ⇧x',
        external: true,
    },
    {
        id: 'inbox.project',
        scope: 'page:inbox',
        group: 'Inbox and Now',
        label: 'Open the item’s project',
        keys: ['g p'],
        external: true,
        overrides: 'global.go.projects',
    },
    {
        id: 'inbox.help',
        scope: 'page:inbox',
        group: 'Inbox and Now',
        label: 'List these keys',
        keys: ['?'],
        external: true,
        overrides: 'global.help',
    },
    // ---- decisions
    {
        id: 'decision.pick',
        scope: 'page:decision',
        group: 'Decisions',
        label: 'Pick option 1–9',
        keys: ['1'],
        display: '1–9',
        external: true,
    },
    {
        id: 'decision.suggested',
        scope: 'page:decision',
        group: 'Decisions',
        label: 'Accept the suggested answer',
        keys: ['s'],
        external: true,
    },
    {
        id: 'decision.move',
        scope: 'page:decision',
        group: 'Decisions',
        label: 'Move between options',
        keys: ['ArrowUp', 'ArrowDown'],
        display: '↑ ↓',
        external: true,
    },
    {
        id: 'decision.submit',
        scope: 'page:decision',
        group: 'Decisions',
        label: 'Send a written answer',
        keys: ['Mod+Enter'],
        external: true,
    },
    // ---- message composer
    {
        id: 'composer.model',
        scope: 'component:composer',
        group: 'Message box',
        label: 'Model picker',
        keys: ['Alt+M'],
    },
    {
        id: 'composer.dictate',
        scope: 'component:composer',
        group: 'Message box',
        label: 'Dictate',
        keys: ['Alt+D'],
    },
    {
        id: 'composer.attach',
        scope: 'component:composer',
        group: 'Message box',
        label: 'Attach a file',
        keys: ['Alt+F'],
    },
    {
        id: 'composer.stop',
        scope: 'component:composer',
        group: 'Message box',
        label: 'Stop the run',
        keys: ['Alt+X'],
    },
    {
        id: 'composer.send',
        scope: 'component:composer',
        group: 'Message box',
        label: 'Send (Shift+Enter for a new line)',
        keys: ['Enter'],
        external: true,
        overrides: 'inbox.open',
    },
    // ---- capture dialog
    {
        id: 'capture.send',
        scope: 'dialog:capture',
        group: 'Capture',
        label: 'Capture / send',
        keys: ['Mod+Enter'],
    },
    {
        id: 'capture.idea',
        scope: 'dialog:capture',
        group: 'Capture',
        label: 'Idea mode',
        keys: ['Alt+1'],
    },
    {
        id: 'capture.direct',
        scope: 'dialog:capture',
        group: 'Capture',
        label: 'Direct mode',
        keys: ['Alt+2'],
    },
    {
        id: 'capture.dictate',
        scope: 'dialog:capture',
        group: 'Capture',
        label: 'Dictate',
        keys: ['Alt+D'],
    },
    {
        id: 'capture.photo',
        scope: 'dialog:capture',
        group: 'Capture',
        label: 'Screenshot or photo',
        keys: ['Alt+S'],
    },
    {
        id: 'capture.attach',
        scope: 'dialog:capture',
        group: 'Capture',
        label: 'Attach a file',
        keys: ['Alt+F'],
    },
    {
        id: 'capture.target',
        scope: 'dialog:capture',
        group: 'Capture',
        label: 'Choose where it goes',
        keys: ['Alt+P'],
    },
    {
        id: 'capture.pick',
        scope: 'dialog:capture',
        group: 'Capture',
        label: 'In the list: filter, move, choose',
        keys: ['ArrowDown'],
        display: 'type ↑ ↓ Enter',
        external: true,
    },
    // ---- new session dialog
    {
        id: 'start.submit',
        scope: 'dialog:start',
        group: 'New session',
        label: 'Start session',
        keys: ['Mod+Enter'],
    },
    {
        id: 'start.project',
        scope: 'dialog:start',
        group: 'New session',
        label: 'Next project',
        keys: ['Alt+P'],
    },
    {
        id: 'start.projectPrev',
        scope: 'dialog:start',
        group: 'New session',
        label: 'Previous project',
        keys: ['Alt+Shift+P'],
    },
    {
        id: 'start.agent',
        scope: 'dialog:start',
        group: 'New session',
        label: 'Switch agent',
        keys: ['Alt+A'],
    },
    {
        id: 'start.model',
        scope: 'dialog:start',
        group: 'New session',
        label: 'Model',
        keys: ['Alt+M'],
    },
    // ---- palette
    {
        id: 'palette.pick',
        scope: 'dialog:palette',
        group: 'Command palette',
        label: 'Move, run',
        keys: ['ArrowDown'],
        display: '↑ ↓ Enter',
        external: true,
    },
    // ---- cheat sheet
    {
        id: 'cheatsheet.search',
        scope: 'dialog:cheatsheet',
        group: 'Keyboard shortcuts',
        label: 'Type to search',
        keys: ['Escape'],
        external: true,
    },
];
/** The live catalogue. Importing this validates it (reserved combos, overlaps). */
export const registry = new ShortcutRegistry(SHORTCUT_DEFS);
//# sourceMappingURL=catalog.js.map